import { useState } from 'react';
import { Link } from 'react-router-dom';

const freeFeatures = [
  'Full access to the 3-hours summit',
  'Live AI demonstrations',
  'Expert-led sessions',
  'Participation in Q&A',
  'Access to essential resources',
];

const premiumFeatures = [
  'Certificate of Participation',
  'Access to GNA AI Business Community',
  'Discounted AI Powered Website/App Solutions',
  'Exclusive Post-Summit Implementation Session',
  // 'Premium templates & automation workflows',
  'Priority access to AI consulting',
];

const RegistrationSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    country: '',
    access: 'free',
  });
  
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const sendOTP = async () => {
    if (!formData.email) {
      alert("Enter email first");
      return;
    }
  
    setOtpLoading(true);
  
    try {
      const res = await fetch("/api/event/send-otp/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email }),
      });
  
      const data = await res.json();
  
      if (!res.ok) {
        if (data.error === "User already registered") {
          setAlreadyRegistered(true);   // 🔥 ADD THIS
          alert("You are already registered. Please check your email.");
          return;
        }
        throw new Error(data.error || "Failed to send OTP");
      }
  
      setOtpSent(true);
      alert("OTP sent to your email");
  
    } catch (err: any) {
      alert(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) {
      alert("Enter OTP");
      return;
    }
  
    try {
      const res = await fetch("/api/event/verify-otp/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          code: otp,
        }),
      });
  
      const data = await res.json();
  
      if (!res.ok) throw new Error(data.error || "Invalid OTP");
  
      setVerified(true);
      alert("Email verified");
  
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  
    if (e.target.name === "email") {
      setAlreadyRegistered(false);
      setOtpSent(false);
      setVerified(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  
    if (!verified) {
      alert("Please verify your email first");
      return;
    }
  
    setLoading(true);
  
    try {
      const res = await fetch("/api/event/register/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          full_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          business_type: formData.business,
          access: "free",
        }),
      });
  
      const data = await res.json();
  
      if (!res.ok) throw new Error(data.error || "Registration failed");
  
      setSubmitted(true);
  
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="register" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold uppercase tracking-widest mb-4">
            Registration &amp; Access
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Choose Your
            <span className="bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent"> Experience</span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            From learning to real business transformation — pick the access level that fits your goals.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Free Card */}
          <div className="flex-1 rounded-2xl border-2 border-gray-200 p-8 bg-white hover:-translate-y-1 transition-transform duration-200">
            <div className="flex items-center justify-between mb-6">
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider">Open to All</span>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-50">
                <i className="ri-ticket-line text-green-700 text-lg"></i>
              </div>
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-1">Free Access</h3>
            <p className="text-4xl font-extrabold text-gray-900 mb-6">$0 <span className="text-base font-normal text-gray-400">/ person</span></p>
            <ul className="space-y-3 mb-8">
              {freeFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 mt-0.5 shrink-0">
                    <i className="ri-check-line text-green-700 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="block w-full text-center py-3.5 rounded-xl border-2 border-green-600 text-green-700 font-bold text-sm hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Register Free at event.greatnigerian.org
            </Link>
          </div>

          {/* Premium Card */}
          <div className="flex-1 rounded-2xl border-2 border-green-600 p-8 bg-gradient-to-br from-green-50 to-emerald-50 relative hover:-translate-y-1 transition-transform duration-200">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="px-4 py-1 rounded-full bg-gradient-to-r from-green-700 to-green-800 text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                Recommended
              </span>
            </div>
            <div className="flex items-center justify-between mb-6">
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider">For Serious Owners</span>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100">
                <i className="ri-vip-crown-line text-amber-600 text-lg"></i>
              </div>
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-1">Premium Access</h3>
            <p className="text-4xl font-extrabold bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent mb-6">$100 <span className="text-base font-normal text-gray-400">/ person</span></p>
            <ul className="space-y-3 mb-8">
              {premiumFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 mt-0.5 shrink-0">
                    <i className="ri-check-line text-green-700 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/premium"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="block w-full text-center py-3.5 rounded-xl bg-gradient-to-r from-green-700 to-green-800 text-white font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
            >
              Register &amp; Upgrade to Premium
            </Link>
            <p className="text-center text-gray-500 text-xs mt-3">Premium upgrade available after free registration</p>
          </div>

          {/* Registration Form */}
          <div className="flex-1 rounded-2xl border border-gray-200 p-8 bg-gray-50">
            <h3 className="text-xl font-extrabold text-gray-900 mb-2">Secure Your Spot</h3>
            <p className="text-gray-500 text-sm mb-6">Quick registration — takes less than 60 seconds.</p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                  <i className="ri-check-double-line text-green-700 text-3xl"></i>
                </div>
                <h4 className="text-gray-900 font-bold text-lg mb-2">You're Registered!</h4>
                <p className="text-gray-500 text-sm">Check your email for confirmation details. We'll see you on April 24th!</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit} noValidate
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-600 transition-colors"
                  />
                </div>
                <div>
                  <label>Email Address</label>

                  <div className="flex gap-2">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="flex-1 px-4 py-3 border rounded-xl"
                    />

                    <button
                      type="button"
                      onClick={sendOTP}
                      disabled={otpLoading || alreadyRegistered}
                      className="px-3 bg-green-700 text-white rounded-xl"
                    >
                      {otpLoading ? "Sending..." : "Send Code"}
                    </button>
                  </div>
                </div>

                {alreadyRegistered && (
                  <p className="text-red-500 text-sm mt-1">
                    ⚠️ You are already registered. Check your email.
                  </p>
                )}

                {otpSent && !verified && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="flex-1 px-4 py-3 border rounded-xl"
                    />

                    <button
                      type="button"
                      onClick={verifyOTP}
                      disabled={alreadyRegistered}
                      className="px-3 bg-blue-600 text-white rounded-xl"
                    >
                      Verify
                    </button>
                  </div>
                )}

                {verified && (
                  <p className="text-green-600 text-sm mt-1">
                    ✅ Email verified
                  </p>
                )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+234 801 234 5678"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm"
                />
              </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Business Type</label>
                  <select
                    name="business"
                    required
                    value={formData.business}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-green-600 transition-colors cursor-pointer"
                  >
                    <option value="">Select your business type</option>
                    <option value="SME / Small Business">SME / Small Business</option>
                    <option value="Startup">Startup</option>
                    <option value="Freelancer / Consultant">Freelancer / Consultant</option>
                    <option value="Corporate / Enterprise">Corporate / Enterprise</option>
                    <option value="Non-Profit / NGO">Non-Profit / NGO</option>
                    <option value="Student / Aspiring Entrepreneur">Student / Aspiring Entrepreneur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Country</label>
                  <input
                    type="text"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Your country"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-600 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !verified || alreadyRegistered}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-green-700 to-green-800 text-white font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? 'Registering...' : 'Register for Free →'}
                </button>
                <p className="text-center text-gray-400 text-xs">No credit card required · Instant confirmation</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegistrationSection;
